/**
 * What a completed restore is CALLED, and how a failure is worded.
 *
 * Pulled out of the feed component because it is the one part of the restore
 * flow with no React in it, and the part most easily got wrong: an undelete
 * recreated a whole row, so counting its columns ("39 fields restored") would
 * describe the mechanism rather than what happened.
 */

import type { RestoreAppliedResponse, RestorePlanResponse } from "@/lib/audit/api-contract";

/** Message for an unknown thrown value — never `[object Object]`. */
export function errorMessageOf(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

/**
 * Headline for the success toast.
 *
 * `planKind` (not the applied response) decides undelete-vs-field-revert: the
 * applied response reports every column it wrote, which for a recreated row is
 * the whole table.
 */
export function restoreSummaryLabel(
  planKind: RestorePlanResponse["kind"],
  fieldsWritten: readonly string[]
): string {
  if (planKind === "undelete") return "Record restored";
  return fieldsWritten.length === 1
    ? "1 field restored"
    : `${fieldsWritten.length} fields restored`;
}

/**
 * The cautions that travel with the RESULT, joined for the toast description.
 * Empty means "no description" — a success that says nothing implies the record
 * came back whole, which for an undelete is false, so these are never dropped.
 */
export function restoreCautionText(response: RestoreAppliedResponse): string | null {
  const cautions = response.cautions ?? [];
  return cautions.length > 0 ? cautions.join(" ") : null;
}
